import { FileText } from 'lucide-react'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { cn } from '@/lib/utils/helpers'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import cardStyles from '../styles/documentCard.module.css'
import type {
  TermsDocumentDraftToolbarProps,
  TermsDocumentTemplateBlockProps,
  TermsDeclarationTemplatesGroupProps,
} from '../types'

const EDITOR_WRAPPER_CLASS =
  'w-full min-w-0 [&_.ck.ck-editor]:max-w-full [&_.ck-editor__editable]:min-h-[min(32vh,320px)]'

function DraftToolbar({
  onSaveDraft,
  onDiscardDraft,
  saveDraftDisabled,
  discardDraftDisabled,
  draftSaving,
}: TermsDocumentDraftToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-b border-slate-100 bg-slate-50/70 px-3 py-2">
      <button
        type="button"
        disabled={saveDraftDisabled || draftSaving}
        className={cn(primaryEntityActionButtonClass, 'px-3 py-1.5 text-xs')}
        onClick={onSaveDraft}
      >
        Save Draft
      </button>
      <button
        type="button"
        disabled={discardDraftDisabled || draftSaving}
        className={cn(primaryEntityActionButtonClass, 'px-3 py-1.5 text-xs')}
        onClick={onDiscardDraft}
      >
        Discard Draft
      </button>
    </div>
  )
}

/** Single template block — Angular `email-template-wrap` toolbar + full-width editor. */
export function TermsDocumentTemplateBlock({
  heading,
  icon: Icon = FileText,
  contentLabel,
  editorId,
  value,
  onChange,
  onSaveDraft,
  onDiscardDraft,
  saveDraftDisabled,
  discardDraftDisabled,
  draftSaving,
  error,
  className,
}: TermsDocumentTemplateBlockProps & { icon?: React.ElementType }) {
  return (
    <section className={cn('w-full min-w-0', className)}>
      <div className={cn(cardStyles.card, 'w-full min-w-0 overflow-hidden')}>
        {heading ? (
          <div className={cardStyles.cardHeader}>
            <Icon className={cardStyles.cardIcon} />
            <h3 className={cardStyles.cardTitle}>{heading}</h3>
          </div>
        ) : null}
        <DraftToolbar
          onSaveDraft={onSaveDraft}
          onDiscardDraft={onDiscardDraft}
          saveDraftDisabled={saveDraftDisabled}
          discardDraftDisabled={discardDraftDisabled}
          draftSaving={draftSaving}
        />
        <div className="min-w-0 p-3 sm:p-4">
          {contentLabel ? (
            <label
              htmlFor={editorId}
              className={cardStyles.formLabel}
            >
              {contentLabel}
            </label>
          ) : null}
          <CKEditor4
            id={editorId}
            value={value}
            onChange={onChange}
            className={EDITOR_WRAPPER_CLASS}
          />
        </div>
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </section>
  )
}

/**
 * Section 2 declaration templates — Angular groups both editors in one
 * `email-template-wrap` row (Declaration Template, then resident toolbar + label + editor).
 */
export function TermsDeclarationTemplatesGroup({
  declaration,
  resident,
}: TermsDeclarationTemplatesGroupProps) {
  return (
    <section className="w-full min-w-0">
      <div className={cn(cardStyles.card, 'w-full min-w-0 overflow-hidden')}>
        <div className={cardStyles.cardHeader}>
          <FileText className={cardStyles.cardIcon} />
          <h3 className={cardStyles.cardTitle}>Declaration Template</h3>
        </div>
        <DraftToolbar
          onSaveDraft={declaration.onSaveDraft}
          onDiscardDraft={declaration.onDiscardDraft}
          saveDraftDisabled={declaration.saveDraftDisabled}
          discardDraftDisabled={declaration.discardDraftDisabled}
          draftSaving={declaration.draftSaving}
        />
        <div className="min-w-0 p-3 sm:p-4">
          <CKEditor4
            id={declaration.editorId}
            value={declaration.value}
            onChange={declaration.onChange}
            className={EDITOR_WRAPPER_CLASS}
          />
        </div>

        <div className="border-t border-slate-200">
          <DraftToolbar
            onSaveDraft={resident.onSaveDraft}
            onDiscardDraft={resident.onDiscardDraft}
            saveDraftDisabled={resident.saveDraftDisabled}
            discardDraftDisabled={resident.discardDraftDisabled}
            draftSaving={resident.draftSaving}
          />
          <div className="min-w-0 p-3 sm:p-4">
            <label
              htmlFor={resident.editorId}
              className={cardStyles.formLabel}
            >
              {resident.contentLabel}
            </label>
            <CKEditor4
              id={resident.editorId}
              value={resident.value}
              onChange={resident.onChange}
              className={EDITOR_WRAPPER_CLASS}
            />
          </div>
        </div>
      </div>
      {declaration.error ? (
        <p className="mt-1 text-xs text-red-600">{declaration.error}</p>
      ) : null}
      {resident.error ? <p className="mt-1 text-xs text-red-600">{resident.error}</p> : null}
    </section>
  )
}

